import loadCustomCountries from "@iewccouk/Store/Handlers/Utilities/CustomLoadCountries";
import setDynamicFormFieldValue from "@iewccouk/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@iewccouk/Store/Reducers";
import { DynamicFormContext } from "@iewccouk/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@iewccouk/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@iewccouk/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select from "@insite/mobius/Select";
import React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "Country";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
        countries: (state.customCountriesList?.countries as CountryModel[]) || [],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
    loadCustomCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Salesforce Dynamic Form country field widget. */
const DynamicCountry: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    countries,
    setDynamicFormFieldValue,
    loadCustomCountries,
}) => {
    const { label, hintText, isRequired } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    useRegisterDynamicFormFieldKeys(id, [fieldKey]);

    useEffect(() => {
        loadCustomCountries({});
    }, [loadCustomCountries]);

    const validate = React.useCallback(() => {
        const error = isRequired && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired, label]);

    React.useEffect(() => {
        formContext.validators[fieldKey] = validate;
        if (typeof fieldValue !== "undefined") {
            validate();
        }
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, fieldValue, validate, formContext.validators]);

    return (
        <DynamicFormFieldShell>
            <LoadingOverlay style={{ width: "100%" }} loading={countries.length === 0}>
                <Select
                    label={label}
                    required={isRequired}
                    value={fieldValue || ""}
                    error={errorMessage}
                    onChange={event => setDynamicFormFieldValue({ key: fieldKey, value: event.currentTarget.value })}
                >
                    <option value="">{hintText}</option>
                    {countries.map(country => (
                        <option key={country.id} value={country.name}>
                            {country.name}
                        </option>
                    ))}
                </Select>
            </LoadingOverlay>
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicCountry),
    definition: {
        group: widgetGroup,
        displayName: "Country",
        icon: "globe",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
